import { Activity, Children, createContext, type ReactNode, use, useCallback, useState } from 'react';

import { PostMessageAction, sendMessage } from '@/lib/postMessage';
import { registryStore } from '@/stores/registry';

import { WizardContext, type FlowListName } from '.';

export const FlowListContext = createContext<{
  name?: FlowListName;
  nextFlow: () => void;
  previousFlow: () => void;
}>({
  nextFlow: () => {},
  previousFlow: () => {}
});

type FlowListProps = {
  children: ReactNode;
  name: FlowListName;
};

export const FlowList = ({ children, name }: FlowListProps) => {
  const flowItems = Children.toArray(children);
  const flowCount = flowItems.length;
  const [currentFlow, setCurrentFlow] = useState<number>(0);
  const { flowlistHistory, removeFromHistory, setCurrentFlowList } = use(WizardContext);

  const redirectTo = () => {
    const url = new URL(window.location.href);
    const to = url.searchParams.get('redirectTo');
    const app = url.searchParams.get('app');

    sendMessage(PostMessageAction.REDIRECT, {
      isPending: registryStore.getState().registry.isPending
    });

    if (app) {
      return;
    }

    window.open(to || 'https://orbita.eduzz.com', '_self');
  };

  const nextFlow = () => {
    setCurrentFlow(previous => {
      if (previous + 1 >= flowCount) {
        redirectTo();
        return previous;
      }

      return previous + 1;
    });
  };

  const handleHistory = useCallback(() => {
    if (flowlistHistory.size <= 1) {
      redirectTo();
      return;
    }

    const history = [...flowlistHistory];
    const currentFlowlist = history[history.length - 1];
    const previousFlowlist = history[history.length - 2];

    if (!previousFlowlist) {
      return;
    }

    removeFromHistory(currentFlowlist);
    setCurrentFlowList(previousFlowlist);
  }, [flowlistHistory, removeFromHistory, setCurrentFlowList]);

  const previousFlow = () => {
    setCurrentFlow(previous => {
      if (previous - 1 < 0) {
        handleHistory();
        return previous;
      }

      return previous - 1;
    });
  };

  return (
    <FlowListContext.Provider value={{ name, nextFlow, previousFlow }}>
      {flowItems.map((flow, index) => (
        <Activity key={index} mode={currentFlow === index ? 'visible' : 'hidden'}>
          {flow}
        </Activity>
      ))}
    </FlowListContext.Provider>
  );
};
